import {
  HttpContextToken,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { createIdempotencyKey } from 'app/core/utils/idempotency.util';

/** Opt out of Idempotency-Key (e.g. read-only report POSTs). */
export const SKIP_IDEMPOTENCY = new HttpContextToken<boolean>(() => false);

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const NO_IDEMPOTENCY_URLS = [
  '/Auth/login',
  '/Auth/company-signup',
  '/Auth/company-signup/stream',
  '/Auth/logout',
  '/hr/document-signing',
  '/FileUpload/single',
  '/FileUpload/multiple',
];

function hasIdempotencyHeader(req: HttpRequest<unknown>): boolean {
  return (
    req.headers.has('Idempotency-Key') || req.headers.has('X-Idempotency-Key')
  );
}

function isMultipart(req: HttpRequest<unknown>): boolean {
  if (req.body instanceof FormData) {
    return true;
  }
  const contentType = req.headers.get('Content-Type') ?? '';
  return contentType.toLowerCase().includes('multipart/form-data');
}

/**
 * Attaches a unique Idempotency-Key on every authenticated mutating request.
 * Preserves a key already set by the caller (same click / retry).
 */
export const idempotencyInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  if (!MUTATING_METHODS.has(req.method.toUpperCase())) {
    return next(req);
  }

  if (req.context.get(SKIP_IDEMPOTENCY)) {
    return next(req);
  }

  if (hasIdempotencyHeader(req) || isMultipart(req)) {
    return next(req);
  }

  if (NO_IDEMPOTENCY_URLS.some((url) => req.url.includes(url))) {
    return next(req);
  }

  const authService = inject(AuthService);
  const token = authService.accessToken;
  if (!token || AuthUtils.isTokenExpired(token)) {
    return next(req);
  }

  return next(
    req.clone({
      setHeaders: {
        'Idempotency-Key': createIdempotencyKey(),
      },
    })
  );
};
